import { ScheduledPost } from "../../../../../database/community/scheduledPosts";
import {
  AccountBaseline,
  ContentConcept,
  ContentEngineConfig,
  TrendOutlier,
  TrendReport,
} from "../../../../../database/community/contentEngine";

// Pure derivations for the Home dashboard. Everything here is honest math
// over Firestore docs: last-run facts come from real timestamps, never from
// simulated activity.

export type EngagementTotals = {
  views: number;
  likes: number;
  comments: number;
  measured: number;
};

const emptyTotals = (): EngagementTotals => ({
  views: 0,
  likes: 0,
  comments: 0,
  measured: 0,
});

const addMetric = (acc: EngagementTotals, m: Record<string, number>): boolean => {
  let counted = false;
  if (typeof m.views === "number") {
    acc.views += m.views;
    counted = true;
  }
  if (typeof m.likes === "number") {
    acc.likes += m.likes;
  }
  if (typeof m.comments === "number") {
    acc.comments += m.comments;
  }
  return counted;
};

export const sumEngagement = (posts: ScheduledPost[]): EngagementTotals => {
  const total = emptyTotals();
  posts.forEach((p) => {
    if (!p.metrics) return;
    let counted = false;
    Object.values(p.metrics).forEach((m) => {
      if (addMetric(total, m)) counted = true;
    });
    if (counted) total.measured += 1;
  });
  return total;
};

export const compact = (n: number): string => {
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

export const timeLabel = (iso: string | null): string => {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  if (sameDay) return `Today ${time}`;
  return `${d.toLocaleDateString([], { weekday: "short", month: "short", day: "numeric" })} ${time}`;
};

// Views count displays/plays. Reach counts unique accounts and is never a substitute.
export const postViews = (p: ScheduledPost): number => {
  if (!p.metrics) return 0;
  return Object.values(p.metrics).reduce((total, m) =>
    total + (typeof m.views === "number" && Number.isFinite(m.views) ? m.views : 0), 0);
};

export const sentWithMetrics = (posts: ScheduledPost[]): ScheduledPost[] =>
  posts
    .filter(
      (p) => (p.status === "sent" || p.status === "partial") &&
        p.metrics !== null && Object.values(p.metrics).some(m => typeof m.views === "number" && Number.isFinite(m.views))
    )
    .sort((a, b) => (a.dueAt || "").localeCompare(b.dueAt || ""));

export type BarDatum = {
  post: ScheduledPost;
  views: number;
  dateLabel: string;
};

export const barChartData = (posts: ScheduledPost[], count = 12): BarDatum[] =>
  sentWithMetrics(posts)
    .slice(-count)
    .map((post) => ({
      post,
      views: postViews(post),
      dateLabel: post.dueAt
        ? new Date(post.dueAt).toLocaleDateString([], {
            month: "short",
            day: "numeric",
          })
        : "",
    }));

// A bar counts as a winner when it clears the content engine's winnerViews
// threshold, or 1.5x the median of the visible window when no config exists.
export const highlightThreshold = (
  views: number[],
  winnerViews?: number
): number => {
  if (typeof winnerViews === "number" && Number.isFinite(winnerViews) && winnerViews > 0) {
    return winnerViews;
  }
  const sorted = views.filter((v) => v > 0).sort((a, b) => a - b);
  if (sorted.length === 0) return Infinity;
  const median = sorted[Math.floor(sorted.length / 2)];
  return median * 1.5;
};

const startOfWeek = (): Date => {
  const d = new Date();
  const day = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
};

export type StatStripData = {
  views: number;
  avgViews: number;
  engagementRate: number | null;
  measured: number;
  postedThisWeek: number;
  plannedThisWeek: number | null;
};

export const statStripData = (
  posts: ScheduledPost[],
  config: ContentEngineConfig | null
): StatStripData => {
  const totals = sumEngagement(posts);
  const weekStart = startOfWeek().toISOString();
  const postedThisWeek = posts.filter(
    (p) =>
      (p.status === "sent" || p.status === "partial") &&
      p.dueAt !== null &&
      p.dueAt >= weekStart
  ).length;
  const planned = config?.cadence?.postsPerWeek;
  return {
    views: totals.views,
    avgViews: totals.measured > 0 ? Math.round(totals.views / totals.measured) : 0,
    engagementRate:
      totals.views > 0 ? (totals.likes + totals.comments) / totals.views : null,
    measured: totals.measured,
    postedThisWeek,
    plannedThisWeek:
      typeof planned === "number" && Number.isFinite(planned) ? planned : null,
  };
};

// Likes + comments summed across platforms, for the engagement drill-down.
export const postEngagement = (
  p: ScheduledPost
): { likes: number; comments: number } => {
  const out = { likes: 0, comments: 0 };
  if (!p.metrics) return out;
  Object.values(p.metrics).forEach((m) => {
    if (typeof m.likes === "number") out.likes += m.likes;
    if (typeof m.comments === "number") out.comments += m.comments;
  });
  return out;
};

export type WeekPosts = {
  posted: ScheduledPost[];
  upcoming: ScheduledPost[];
};

// What "posts this week" is made of: already out plus still scheduled
// before next Monday.
export const weekPosts = (posts: ScheduledPost[]): WeekPosts => {
  const start = startOfWeek();
  const end = new Date(start);
  end.setDate(end.getDate() + 7);
  const startIso = start.toISOString();
  const endIso = end.toISOString();
  const inWeek = (p: ScheduledPost) =>
    p.dueAt !== null && p.dueAt >= startIso && p.dueAt < endIso;
  return {
    posted: posts
      .filter((p) => (p.status === "sent" || p.status === "partial") && inWeek(p))
      .sort((a, b) => (a.dueAt || "").localeCompare(b.dueAt || "")),
    upcoming: posts
      .filter(
        (p) =>
          (p.status === "scheduled" ||
            p.status === "publishing" ||
            p.status === "draft") &&
          inWeek(p)
      )
      .sort((a, b) => (a.dueAt || "").localeCompare(b.dueAt || "")),
  };
};

export const topPosts = (posts: ScheduledPost[], n = 5): BarDatum[] =>
  sentWithMetrics(posts)
    .map((post) => ({
      post,
      views: postViews(post),
      dateLabel: post.dueAt
        ? new Date(post.dueAt).toLocaleDateString([], {
            month: "short",
            day: "numeric",
          })
        : "",
    }))
    .sort((a, b) => b.views - a.views)
    .slice(0, n);

// ── Niche panel ──────────────────────────────────────────────────────────────

export const platformFromUrl = (url?: string): string | null => {
  if (!url) return null;
  try {
    const host = new URL(url).hostname.replace(/^www\./, "");
    if (host.includes("tiktok")) return "TikTok";
    if (host.includes("instagram")) return "Instagram";
    if (host.includes("linkedin")) return "LinkedIn";
    if (host.includes("youtube") || host === "youtu.be") return "YouTube";
    if (host === "x.com" || host.includes("twitter")) return "X";
    if (host.includes("facebook")) return "Facebook";
    return host;
  } catch {
    return null;
  }
};

export type LabeledOutlier = TrendOutlier & {
  platform: string | null;
  reportId: string;
};

// Outliers across the recent reports, newest report first, deduped by url,
// each labeled with the platform its url points at.
export const mergedOutliers = (
  reports: TrendReport[],
  limit = 8
): LabeledOutlier[] => {
  const seen = new Set<string>();
  const out: LabeledOutlier[] = [];
  reports.forEach((r) => {
    r.outliers.forEach((o) => {
      const key = o.url || `${r.id}:${o.account}:${o.title}`;
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ ...o, platform: platformFromUrl(o.url), reportId: r.id });
    });
  });
  return out.sort((a, b) => (b.views || 0) - (a.views || 0)).slice(0, limit);
};

export type NicheRow = AccountBaseline & {
  isSelf: boolean;
  topUrl: string | null;
  platform: string | null;
};

export const nicheRanking = (
  baselines: AccountBaseline[],
  outliers: LabeledOutlier[],
  own: { avgViews: number; topViews: number } | null,
  ownHandles: string[],
  ownLabel: string
): NicheRow[] => {
  const norm = (s: string) => s.replace(/^@/, "").toLowerCase();
  const ownSet = new Set(ownHandles.map(norm));
  const topByAccount = new Map<string, { views: number; url: string | null; platform: string | null }>();
  outliers.forEach((o) => {
    if (!o.account) return;
    const key = norm(o.account);
    const prev = topByAccount.get(key);
    if (!prev || (o.views || 0) > prev.views) {
      topByAccount.set(key, {
        views: o.views || 0,
        url: o.url || null,
        platform: o.platform,
      });
    }
  });

  const rows: NicheRow[] = baselines.map((b) => {
    const top = topByAccount.get(norm(b.account));
    return {
      ...b,
      topViews: b.topViews ?? (top ? top.views : null),
      isSelf: ownSet.has(norm(b.account)),
      topUrl: top?.url || null,
      platform: top?.platform || null,
    };
  });

  if (own && !rows.some((r) => r.isSelf)) {
    rows.push({
      account: ownLabel,
      avgViews: own.avgViews,
      topViews: own.topViews,
      isSelf: true,
      topUrl: null,
      platform: null,
    });
  }

  return rows.sort((a, b) => (b.avgViews || 0) - (a.avgViews || 0));
};

export const ownAccountStats = (
  posts: ScheduledPost[]
): { avgViews: number; topViews: number } | null => {
  const data = sentWithMetrics(posts).map(postViews).filter((v) => v > 0);
  if (data.length === 0) return null;
  return {
    avgViews: Math.round(data.reduce((a, b) => a + b, 0) / data.length),
    topViews: Math.max(...data),
  };
};

// ── Agent cards ──────────────────────────────────────────────────────────────

export type AgentFact = {
  headline: string;
  when: string | null;
  empty: boolean;
};

export const trendwatcherFact = (reports: TrendReport[]): AgentFact => {
  const latest = reports[0];
  if (!latest) {
    return {
      headline: "No trend scan yet. The weekly content run creates one.",
      when: null,
      empty: true,
    };
  }
  const scanned = latest.analyzedCount
    ? `Scanned ${latest.analyzedCount} posts`
    : "Scanned the niche";
  const niche = latest.niche ? ` in ${latest.niche}` : "";
  return {
    headline: `${scanned}${niche}`,
    when: latest.createdAt || latest.id,
    empty: false,
  };
};

export const scriptwriterFact = (concepts: ContentConcept[]): AgentFact => {
  if (concepts.length === 0) {
    return {
      headline: "No concepts yet. Hooks land here after the first run.",
      when: null,
      empty: true,
    };
  }
  const weekStart = startOfWeek().toISOString();
  const thisWeek = concepts.filter(
    (c) => (c.createdAt || "") >= weekStart
  ).length;
  const latest = concepts[0];
  return {
    headline:
      thisWeek > 0
        ? `${thisWeek} concept${thisWeek === 1 ? "" : "s"} this week. Latest: ${latest.concept}`
        : `Latest concept: ${latest.concept}`,
    when: latest.createdAt,
    empty: false,
  };
};

export type VerdictCounts = { scale: number; iterate: number; kill: number };

export const analystFact = (
  concepts: ContentConcept[]
): AgentFact & { counts: VerdictCounts } => {
  const counts: VerdictCounts = { scale: 0, iterate: 0, kill: 0 };
  let lastFetch: string | null = null;
  concepts.forEach((c) => {
    const v = c.verdict || c.suggestedVerdict;
    if (v === "SCALE") counts.scale += 1;
    else if (v === "ITERATE") counts.iterate += 1;
    else if (v === "KILL") counts.kill += 1;
    if (c.metricsFetchedAt && (!lastFetch || c.metricsFetchedAt > lastFetch)) {
      lastFetch = c.metricsFetchedAt;
    }
  });
  const judged = counts.scale + counts.iterate + counts.kill;
  if (judged === 0) {
    return {
      headline: "No verdicts yet. Posts get judged once metrics land.",
      when: null,
      empty: true,
      counts,
    };
  }
  return {
    headline: `${judged} concept${judged === 1 ? "" : "s"} judged`,
    when: lastFetch,
    empty: false,
    counts,
  };
};
